/**
 * EstablishmentAnalytics - Vue des analyses au niveau établissement
 * Métriques agrégées par tenant/établissement avec filtres par niveau et classe
 */

import { useState, useMemo } from 'react';
import { Loading } from '@carbon/react';
import {
  useKeycloak,
  useActiveEstablishmentId,
  useAdminStore,
  useAccessStore,
} from '@sphere/auth/data-access';
import {
  useEstablishmentDashboard,
  useEstablishmentByClass,
  useEstablishmentByGrade,
  useEstablishmentTopActivities,
  useEstablishmentDailyActivity,
  useAnalyticsHealth,
} from '@sphere/analytics/data-access';
import { EstablishmentAnalyticsDashboard } from '@sphere/analytics/ui';
import { env } from '@/config/env.config';

export const EstablishmentAnalytics = () => {
  const { authenticated } = useKeycloak();
  const currentEstablishment = useAdminStore(
    (state) => state.currentEstablishment
  );
  const scope = useAccessStore((state) => state.scope);
  const establishmentId = scope.establishmentIds?.[0];
  const [selectedPeriod, setSelectedPeriod] = useState('30');
  // tenantId = establishmentId (même concept)
  const tenantId = establishmentId || env.ANALYTICS_DEFAULT_TENANT;

  const sinceDays = useMemo(
    () => Number(selectedPeriod) || 30,
    [selectedPeriod]
  );

  // Hooks React Query
  const { data: healthData } = useAnalyticsHealth();
  const { data: summaryData, isLoading: isLoadingSummary } =
    useEstablishmentDashboard(tenantId, sinceDays);
  const { data: classesData, isLoading: isLoadingClasses } =
    useEstablishmentByClass(tenantId, sinceDays);
  const { data: gradesData, isLoading: isLoadingGrades } =
    useEstablishmentByGrade(tenantId, sinceDays);
  const { data: topActivitiesData, isLoading: isLoadingTopActivities } =
    useEstablishmentTopActivities(tenantId, sinceDays, 10);
  const { data: dailyActivityData, isLoading: isLoadingDailyActivity } =
    useEstablishmentDailyActivity(tenantId, sinceDays);

  const isLoading =
    isLoadingSummary ||
    isLoadingClasses ||
    isLoadingGrades ||
    isLoadingTopActivities ||
    isLoadingDailyActivity;

  const handleRefresh = () => {
    // React Query refetch will be handled automatically when dependencies change
    window.location.reload();
  };

  if (isLoading) {
    return (
      <Loading
        description="Chargement des analytics d'établissement..."
        withOverlay
      />
    );
  }

  if (!authenticated || !tenantId || !summaryData) {
    return null;
  }

  const summary = summaryData.summary;
  const dailyActivity = [...(dailyActivityData?.daily_metrics ?? [])].reverse();
  const topActivities = topActivitiesData;
  const classes = classesData?.classes ?? [];
  const grades = gradesData?.grades ?? [];

  const health = healthData ? healthData.status : null;

  return (
    <EstablishmentAnalyticsDashboard
      summary={summary}
      dailyActivity={dailyActivity}
      topActivities={topActivities}
      classes={classes}
      grades={grades}
      selectedPeriod={selectedPeriod}
      onPeriodChange={setSelectedPeriod}
      onRefresh={handleRefresh}
      health={health}
      error={null}
    />
  );
};

export default EstablishmentAnalytics;
